import * as SheetPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import { cn } from '../lib/utils';

function Sheet({ dataTestId, ...props }) {
  return <SheetPrimitive.Root data-slot="sheet" data-testid={dataTestId} {...props} />;
}

function SheetTrigger({ dataTestId, ...props }) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" data-testid={dataTestId} {...props} />;
}

function SheetClose({ dataTestId, ...props }) {
  return <SheetPrimitive.Close data-slot="sheet-close" data-testid={dataTestId} {...props} />;
}

function SheetPortal({ dataTestId, ...props }) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" data-testid={dataTestId} {...props} />;
}

function SheetOverlay({ className, dataTestId, ...props }) {
  return (
    <SheetPrimitive.Overlay
      data-testid={dataTestId}
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-overlay',
        className
      )}
      {...props}
    />
  );
}

// `closeButtonProps` is spread onto the built-in close button so a caller can give it a test id or
// disable it (e.g. while a save is in flight) — parity with DialogContent.
function SheetContent({ className, children, side = 'right', dataTestId, closeButtonProps, ...props }) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-testid={dataTestId}
        className={cn(
          'bg-surface-panel fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out',
          side === 'right' &&
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm',
          side === 'left' &&
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
          side === 'top' &&
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b',
          side === 'bottom' &&
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t',
          className
        )}
        {...props}
      >
        {children}
        {/* Same treatment as DialogContent's close button, so drawers and modals match.
            Rendered AFTER children, and z-10: a drawer whose content is itself positioned (e.g.
            ProfileModal's `relative` wrapper) would otherwise paint over this button and swallow
            its clicks — the button stayed visible but stopped closing the drawer. */}
        <SheetPrimitive.Close
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center cursor-pointer justify-center rounded-full border border-close-line bg-action-chip-bg text-chip-info-fg transition-colors disabled:pointer-events-none disabled:opacity-50"
          {...closeButtonProps}
        >
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, dataTestId, ...props }) {
  return <div data-slot="sheet-header" data-testid={dataTestId} className={cn('flex flex-col gap-1.5 p-6', className)} {...props} />;
}

function SheetFooter({ className, dataTestId, ...props }) {
  return (
    <div
      data-slot="sheet-footer"
      data-testid={dataTestId}
      // Same action sizing and same reason as DialogFooter — a drawer footer and a modal footer
      // are the same control strip.
      className={cn('mt-auto flex flex-col gap-2 p-6 [&_button]:h-12 [&_button]:text-base', className)}
      {...props}
    />
  );
}

function SheetTitle({ className, dataTestId, ...props }) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      data-testid={dataTestId}
      // One header treatment for every drawer and modal: 24px bold --fg. Set on the primitive
      // rather than per call site, so a new drawer is consistent without being told to be.
      className={cn('text-2xl font-bold text-fg', className)}
      {...props}
    />
  );
}

function SheetDescription({ className, dataTestId, ...props }) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      data-testid={dataTestId}
      // 14px regular --fg-secondary, the subtext half of the shared header treatment.
      className={cn('text-sm text-fg-secondary', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
